import React, { useMemo, useState } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useApp } from '../context/AppContext';
import { Settings, Clock, Zap, Trophy, X, Filter, Check, Gem, Star, Search, ChevronDown, MapPin, Calendar, Users, Video, Info, Award, Shield, FileCheck } from 'lucide-react';
import { BottomNav } from '../components/BottomNav';
interface Activity {
  id: string;
  emoji: string;
  title: string;
  points: number;
  type: 'gold' | 'diamond';
  difficulty: 'Easy' | 'Medium' | 'Hard';
  time: string;
  description?: string;
  instructions?: string[];
  impact?: string;
  completionRate?: number;
}
interface GroupEvent {
  id: string;
  emoji: string;
  title: string;
  points: number;
  type: 'gold' | 'diamond';
  eventType: 'online' | 'offline';
  date: string;
  time: string;
  location?: string;
  participants: number;
  maxParticipants: number;
  description: string;
  organizer: string;
}
interface Category {
  id: string;
  title: string;
  activities: Activity[];
}
const categories: Category[] = [{
  id: 'transport',
  title: '🚗 Trasporti',
  activities: [{
    id: 't1',
    emoji: '🚲',
    title: 'Andare al lavoro in bici',
    points: 50,
    type: 'diamond',
    difficulty: 'Medium',
    time: '30m',
    description: "Scegli la bicicletta invece dell'auto per i tuoi spostamenti quotidiani. Un modo sano ed ecologico per muoverti.",
    instructions: ['Pianifica il percorso più sicuro e comodo', 'Controlla che la bici sia in buone condizioni', 'Indossa un casco e abbigliamento visibile', "Registra il tuo viaggio nell'app"],
    impact: "Ogni km in bici risparmia circa 0.21 kg di CO₂ rispetto all'auto. Un tragitto medio di 10 km evita 2.1 kg di emissioni.",
    completionRate: 68
  }, {
    id: 't2',
    emoji: '🚌',
    title: 'Usare i mezzi pubblici',
    points: 30,
    type: 'gold',
    difficulty: 'Easy',
    time: '45m',
    description: 'Scegli autobus, tram o metro per i tuoi spostamenti. Riduci il traffico e le emissioni.',
    instructions: ['Controlla gli orari e pianifica il viaggio', 'Acquista il biglietto in anticipo', 'Arriva alla fermata qualche minuto prima', "Conferma il viaggio nell'app"],
    impact: 'I mezzi pubblici producono il 45% in meno di CO₂ per passeggero rispetto alle auto private.',
    completionRate: 72
  }, {
    id: 't3',
    emoji: '🚙',
    title: "Condividere l'auto",
    points: 40,
    type: 'diamond',
    difficulty: 'Medium',
    time: '30m',
    description: 'Organizza un car pooling con colleghi o amici che fanno lo stesso percorso.',
    instructions: ['Trova persone con percorsi simili', 'Stabilisci orari e punti di incontro', 'Alterna chi guida per condividere i costi', 'Registra il viaggio condiviso'],
    impact: "Condividere l'auto con 3 persone riduce le emissioni del 75% per passeggero.",
    completionRate: 45
  }, {
    id: 't4',
    emoji: '🚶',
    title: 'Andare a piedi',
    points: 25,
    type: 'gold',
    difficulty: 'Medium',
    time: '20m',
    description: 'Per brevi distanze, scegli di camminare. Zero emissioni e benefici per la salute.',
    instructions: ['Pianifica percorsi pedonali sicuri', 'Indossa scarpe comode', 'Porta con te acqua se fa caldo', "Traccia i tuoi passi nell'app"],
    impact: "Camminare 2 km al giorno invece di guidare risparmia circa 150 kg di CO₂ all'anno.",
    completionRate: 81
  }]
}, {
  id: 'energy',
  title: '⚡ Energia',
  activities: [{
    id: 'e1',
    emoji: '💡',
    title: 'Installare lampadine LED',
    points: 20,
    type: 'gold',
    difficulty: 'Easy',
    time: '10m',
    description: 'Sostituisci le vecchie lampadine con LED ad alta efficienza energetica.',
    instructions: ['Conta quante lampadine devi sostituire', 'Acquista LED della giusta potenza', "Spegni l'interruttore prima di sostituire", 'Smaltisci correttamente le vecchie lampadine'],
    impact: "Le lampadine LED consumano l'80% in meno di energia e durano 25 volte di più delle tradizionali.",
    completionRate: 76
  }, {
    id: 'e2',
    emoji: '🔌',
    title: 'Staccare i dispositivi',
    points: 15,
    type: 'gold',
    difficulty: 'Easy',
    time: '1m',
    description: 'Scollega caricabatterie e dispositivi non in uso per evitare il consumo fantasma.',
    instructions: ['Identifica i dispositivi sempre collegati', 'Usa ciabatte con interruttore', 'Stacca caricabatterie quando non in uso', "Fai diventare un'abitudine quotidiana"],
    impact: 'Il consumo fantasma rappresenta il 5-10% della bolletta elettrica domestica.',
    completionRate: 62
  }, {
    id: 'e3',
    emoji: '☀️',
    title: 'Usare energia solare',
    points: 100,
    type: 'diamond',
    difficulty: 'Hard',
    time: '1g',
    description: 'Installa pannelli solari o passa a un fornitore di energia rinnovabile.',
    instructions: ['Valuta lo spazio disponibile sul tetto', 'Richiedi preventivi a installatori certificati', 'Verifica incentivi e detrazioni fiscali', 'Monitora la produzione di energia'],
    impact: 'I pannelli solari possono ridurre le emissioni domestiche del 80% e ammortizzarsi in 7-10 anni.',
    completionRate: 23
  }, {
    id: 'e4',
    emoji: '🌡️',
    title: 'Abbassare il termostato',
    points: 25,
    type: 'gold',
    difficulty: 'Easy',
    time: '1m',
    description: 'Riduci la temperatura di 1-2 gradi in inverno per risparmiare energia.',
    instructions: ['Imposta il termostato a 19-20°C', 'Indossa un maglione in più', 'Chiudi le porte delle stanze non usate', 'Usa un termostato programmabile'],
    impact: "Abbassare di 1°C la temperatura riduce i consumi del 6% e risparmia circa 200 kg di CO₂ all'anno.",
    completionRate: 58
  }]
}, {
  id: 'waste',
  title: '♻️ Rifiuti',
  activities: [{
    id: 'w1',
    emoji: '♻️',
    title: 'Fare la raccolta differenziata',
    points: 10,
    type: 'gold',
    difficulty: 'Easy',
    time: '5m',
    description: 'Separa correttamente carta, plastica, vetro e organico per facilitare il riciclo.',
    instructions: ['Impara le regole locali di raccolta', 'Usa contenitori separati in casa', 'Pulisci i contenitori prima di riciclarli', 'Verifica i simboli di riciclabilità'],
    impact: 'Il riciclo riduce le emissioni del 70% rispetto alla produzione da materie prime vergini.',
    completionRate: 85
  }, {
    id: 'w2',
    emoji: '🍂',
    title: 'Fare il compostaggio',
    points: 20,
    type: 'gold',
    difficulty: 'Medium',
    time: '10m',
    description: 'Trasforma gli scarti organici in compost nutriente per le piante.',
    instructions: ['Procurati una compostiera o crea un cumulo', 'Alterna scarti verdi (umidi) e marroni (secchi)', 'Mescola regolarmente per aerare', 'Usa il compost maturo dopo 3-6 mesi'],
    impact: 'Il compostaggio riduce i rifiuti domestici del 30% e produce fertilizzante naturale.',
    completionRate: 41
  }, {
    id: 'w3',
    emoji: '🛍️',
    title: 'Usare borse riutilizzabili',
    points: 15,
    type: 'gold',
    difficulty: 'Easy',
    time: '0m',
    description: 'Porta sempre con te borse riutilizzabili per la spesa.',
    instructions: ['Tieni borse riutilizzabili in auto o in borsa', 'Scegli borse resistenti e lavabili', 'Rifiuta i sacchetti di plastica nei negozi', 'Usa anche sacchetti di stoffa per frutta e verdura'],
    impact: "Una borsa riutilizzabile sostituisce circa 700 sacchetti di plastica all'anno.",
    completionRate: 79
  }, {
    id: 'w4',
    emoji: '🗑️',
    title: 'Giornata zero rifiuti',
    points: 50,
    type: 'diamond',
    difficulty: 'Hard',
    time: '24h',
    description: "Sfida te stesso a non produrre rifiuti per un'intera giornata.",
    instructions: ['Pianifica i pasti con ingredienti sfusi', 'Porta contenitori riutilizzabili', 'Evita prodotti monouso e imballaggi', 'Documenta la tua esperienza'],
    impact: "L'italiano medio produce 1.5 kg di rifiuti al giorno. Una giornata zero rifiuti evita questa produzione.",
    completionRate: 18
  }]
}, {
  id: 'food',
  title: '🍽️ Alimentazione',
  activities: [{
    id: 'f1',
    emoji: '🥗',
    title: 'Pasto a base vegetale',
    points: 30,
    type: 'gold',
    difficulty: 'Medium',
    time: '45m',
    description: 'Scegli un pasto completamente vegetale, senza carne né derivati animali.',
    instructions: ['Esplora ricette vegetali gustose', 'Assicurati di includere proteine (legumi, tofu)', 'Usa verdure di stagione', 'Sperimenta con spezie e sapori nuovi'],
    impact: 'Un pasto vegetale produce il 50% in meno di emissioni rispetto a uno con carne.',
    completionRate: 54
  }, {
    id: 'f2',
    emoji: '🍎',
    title: 'Comprare prodotti locali',
    points: 25,
    type: 'gold',
    difficulty: 'Easy',
    time: '30m',
    description: 'Acquista frutta e verdura da produttori locali o mercati contadini.',
    instructions: ['Cerca mercati contadini nella tua zona', 'Scegli prodotti di stagione', 'Parla con i produttori per conoscere le loro pratiche', 'Porta borse riutilizzabili'],
    impact: "I prodotti locali riducono le emissioni da trasporto del 90% e supportano l'economia locale.",
    completionRate: 67
  }, {
    id: 'f3',
    emoji: '🍱',
    title: 'Preparare i pasti in anticipo',
    points: 20,
    type: 'gold',
    difficulty: 'Medium',
    time: '2h',
    description: 'Cucina in batch per ridurre sprechi e consumo energetico.',
    instructions: ['Pianifica i menu della settimana', 'Fai la spesa una volta sola', 'Cucina porzioni multiple', 'Conserva in contenitori riutilizzabili'],
    impact: "La preparazione in batch riduce gli sprechi alimentari del 40% e ottimizza l'uso dell'energia.",
    completionRate: 48
  }, {
    id: 'f4',
    emoji: '🍌',
    title: 'Ridurre gli sprechi alimentari',
    points: 35,
    type: 'gold',
    difficulty: 'Easy',
    time: '10m',
    description: 'Usa tutti gli alimenti prima che scadano e riutilizza gli avanzi creativamente.',
    instructions: ['Controlla regolarmente il frigo', 'Usa il metodo FIFO (First In, First Out)', 'Congela ciò che non usi subito', 'Trasforma gli avanzi in nuovi piatti'],
    impact: "Lo spreco alimentare rappresenta l'8% delle emissioni globali. Ridurlo ha un impatto enorme.",
    completionRate: 61
  }]
}, {
  id: 'water',
  title: '💧 Acqua',
  activities: [{
    id: 'wa1',
    emoji: '🚿',
    title: 'Fare docce brevi',
    points: 15,
    type: 'gold',
    difficulty: 'Easy',
    time: '5m',
    description: 'Riduci il tempo della doccia a 5 minuti per risparmiare acqua ed energia.',
    instructions: ['Usa un timer o ascolta una canzone', "Chiudi l'acqua mentre ti insaponi", 'Installa un soffione a basso flusso', "Fai diventare un'abitudine"],
    impact: "Una doccia di 5 minuti usa 40 litri d'acqua contro i 150 di un bagno in vasca.",
    completionRate: 52
  }, {
    id: 'wa2',
    emoji: '🔧',
    title: 'Riparare le perdite',
    points: 40,
    type: 'gold',
    difficulty: 'Medium',
    time: '30m',
    description: "Ripara rubinetti e tubi che perdono per evitare sprechi d'acqua.",
    instructions: ['Controlla tutti i rubinetti e le tubature', 'Sostituisci guarnizioni usurate', 'Chiama un idraulico se necessario', 'Verifica periodicamente'],
    impact: "Un rubinetto che gocciola spreca fino a 20.000 litri d'acqua all'anno.",
    completionRate: 38
  }, {
    id: 'wa3',
    emoji: '🌧️',
    title: "Raccogliere l'acqua piovana",
    points: 60,
    type: 'diamond',
    difficulty: 'Hard',
    time: '4h',
    description: "Installa un sistema per raccogliere e riutilizzare l'acqua piovana.",
    instructions: ['Posiziona contenitori sotto le grondaie', 'Usa filtri per rimuovere detriti', 'Copri i contenitori per evitare zanzare', "Usa l'acqua per piante e giardino"],
    impact: "Un sistema di raccolta può fornire il 50% dell'acqua necessaria per il giardino.",
    completionRate: 15
  }, {
    id: 'wa4',
    emoji: '🥤',
    title: 'Usare una borraccia',
    points: 10,
    type: 'gold',
    difficulty: 'Easy',
    time: '0m',
    description: 'Porta sempre con te una borraccia riutilizzabile invece di comprare bottiglie di plastica.',
    instructions: ['Scegli una borraccia di qualità', 'Riempila prima di uscire', 'Lavala regolarmente', 'Tienila sempre in borsa o zaino'],
    impact: "Una borraccia sostituisce circa 150 bottiglie di plastica all'anno.",
    completionRate: 73
  }]
}, {
  id: 'nature',
  title: '🌳 Natura',
  activities: [{
    id: 'n1',
    emoji: '🌳',
    title: 'Piantare alberi',
    points: 80,
    type: 'diamond',
    difficulty: 'Hard',
    time: '2h',
    description: 'Partecipa a iniziative di riforestazione o pianta alberi nel tuo giardino.',
    instructions: ['Scegli specie native della tua zona', 'Prepara il terreno adeguatamente', "Pianta nel periodo giusto dell'anno", "Prenditi cura dell'albero nei primi anni"],
    impact: "Un albero assorbe circa 22 kg di CO₂ all'anno e produce ossigeno per 2 persone.",
    completionRate: 29
  }, {
    id: 'n2',
    emoji: '🧹',
    title: 'Pulizia ambientale',
    points: 50,
    type: 'diamond',
    difficulty: 'Medium',
    time: '1h',
    description: 'Partecipa a iniziative di pulizia di parchi, spiagge o aree naturali.',
    instructions: ['Unisciti a gruppi locali di volontariato', 'Porta guanti e sacchi per la raccolta', 'Separa i rifiuti raccolti per il riciclo', "Documenta l'impatto con foto prima/dopo"],
    impact: 'Le pulizie ambientali rimuovono tonnellate di rifiuti che danneggerebbero gli ecosistemi.',
    completionRate: 34
  }, {
    id: 'n3',
    emoji: '🦋',
    title: 'Supportare la fauna locale',
    points: 30,
    type: 'gold',
    difficulty: 'Easy',
    time: '20m',
    description: 'Crea habitat per insetti impollinatori e uccelli nel tuo giardino o balcone.',
    instructions: ['Pianta fiori che attirano impollinatori', 'Installa casette per uccelli', 'Evita pesticidi chimici', 'Fornisci acqua fresca'],
    impact: 'Gli impollinatori sono essenziali per il 75% delle colture alimentari globali.',
    completionRate: 44
  }, {
    id: 'n4',
    emoji: '🌻',
    title: 'Curare un orto',
    points: 40,
    type: 'gold',
    difficulty: 'Medium',
    time: '1h',
    description: 'Coltiva le tue verdure e erbe aromatiche in giardino o in vaso.',
    instructions: ['Inizia con piante facili (pomodori, basilico)', 'Usa compost come fertilizzante', 'Annaffia regolarmente ma senza sprechi', 'Raccogli e gusta i tuoi prodotti'],
    impact: 'Un orto domestico riduce le emissioni da trasporto e imballaggio del cibo.',
    completionRate: 37
  }]
}];
const groupEvents: GroupEvent[] = [{
  id: 'ge1',
  emoji: '🌳',
  title: 'Giornata di Riforestazione Urbana',
  points: 150,
  type: 'diamond',
  eventType: 'offline',
  date: '2026-06-15',
  time: '09:00',
  location: 'Parco Sempione, Milano',
  participants: 24,
  maxParticipants: 30,
  description: 'Unisciti a noi per piantare alberi nel parco cittadino. Forniremo tutti gli strumenti necessari.',
  organizer: 'Green Milano'
}, {
  id: 'ge2',
  emoji: '🧹',
  title: 'Pulizia Spiaggia Comunitaria',
  points: 100,
  type: 'diamond',
  eventType: 'offline',
  date: '2026-06-18',
  time: '10:00',
  location: 'Spiaggia di Ostia, Roma',
  participants: 18,
  maxParticipants: 25,
  description: 'Aiutaci a pulire la spiaggia e proteggere la vita marina. Guanti e sacchi forniti.',
  organizer: 'Oceano Pulito'
}, {
  id: 'ge3',
  emoji: '💡',
  title: 'Workshop: Energia Rinnovabile in Casa',
  points: 80,
  type: 'diamond',
  eventType: 'online',
  date: '2026-06-20',
  time: '18:30',
  participants: 45,
  maxParticipants: 50,
  description: 'Webinar interattivo su come ridurre i consumi energetici e installare pannelli solari.',
  organizer: 'Energia Verde Italia'
}, {
  id: 'ge4',
  emoji: '🚲',
  title: 'Critical Mass - Pedalata Collettiva',
  points: 60,
  type: 'gold',
  eventType: 'offline',
  date: '2026-06-22',
  time: '19:00',
  location: 'Piazza Duomo, Milano',
  participants: 67,
  maxParticipants: 100,
  description: 'Pedalata di gruppo per promuovere la mobilità sostenibile in città.',
  organizer: 'Bike Milano'
}, {
  id: 'ge5',
  emoji: '♻️',
  title: 'Laboratorio di Riciclo Creativo',
  points: 50,
  type: 'gold',
  eventType: 'offline',
  date: '2026-06-25',
  time: '15:00',
  location: 'Centro Sociale, Torino',
  participants: 12,
  maxParticipants: 20,
  description: 'Impara a trasformare oggetti di scarto in creazioni artistiche e utili.',
  organizer: 'RiCreo Torino'
}, {
  id: 'ge6',
  emoji: '🌱',
  title: 'Webinar: Orto Urbano per Principianti',
  points: 40,
  type: 'gold',
  eventType: 'online',
  date: '2026-06-27',
  time: '20:00',
  participants: 89,
  maxParticipants: 100,
  description: 'Scopri come coltivare verdure fresche sul tuo balcone o terrazzo.',
  organizer: 'Orti Urbani Network'
}, {
  id: 'ge7',
  emoji: '🍃',
  title: 'Swap Party - Scambio Vestiti',
  points: 70,
  type: 'gold',
  eventType: 'offline',
  date: '2026-07-02',
  time: '16:00',
  location: 'Spazio Condiviso, Bologna',
  participants: 28,
  maxParticipants: 40,
  description: 'Porta vestiti che non usi più e scambiali con altri. Moda sostenibile e circolare!',
  organizer: 'Fashion Revolution Bologna'
}, {
  id: 'ge8',
  emoji: '🌊',
  title: 'Conferenza: Crisi Climatica e Azioni Concrete',
  points: 90,
  type: 'diamond',
  eventType: 'online',
  date: '2026-07-05',
  time: '19:00',
  participants: 156,
  maxParticipants: 200,
  description: 'Esperti internazionali discutono soluzioni pratiche alla crisi climatica.',
  organizer: 'Climate Action Italia'
}];
interface ImpactPageProps {
  onNavigate: (page: string, activity?: Activity) => void;
}
export function ImpactPage({
  onNavigate
}: ImpactPageProps) {
  const [activeTab, setActiveTab] = useState<'solo' | 'group' | 'challenges'>('solo');
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [openCategories, setOpenCategories] = useState<string[]>(categories.map(cat => cat.id));
  const [selectedRewardTypes, setSelectedRewardTypes] = useState<('gold' | 'diamond')[]>([]);
  const [selectedDifficulties, setSelectedDifficulties] = useState<('Easy' | 'Medium' | 'Hard')[]>([]);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [isCreditsClosing, setIsCreditsClosing] = useState(false);
  const {
    wallet,
    challenges,
    acceptChallenge,
    showToast,
    actions
  } = useApp();
  // Filter challenges by status
  const activeChallenges = challenges.filter(c => c.status === 'active');
  const availableChallenges = challenges.filter(c => c.status === 'available');
  // Get user's action titles to filter duplicates
  const userActionTitles = actions.map(a => a.title.toLowerCase());
  const handleAcceptChallenge = (challengeId: string) => {
    acceptChallenge(challengeId);
    showToast({
      variant: 'success',
      message: 'Sfida aggiunta!'
    });
  };
  const toggleCategory = (categoryId: string) => {
    setOpenCategories(prev => prev.includes(categoryId) ? prev.filter(id => id !== categoryId) : [...prev, categoryId]);
  };
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const scrollTop = e.currentTarget.scrollTop;
    setIsScrolled(scrollTop > 100);
  };
  const toggleRewardFilter = (type: 'gold' | 'diamond') => {
    setSelectedRewardTypes(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]);
  };
  const toggleDifficultyFilter = (difficulty: 'Easy' | 'Medium' | 'Hard') => {
    setSelectedDifficulties(prev => prev.includes(difficulty) ? prev.filter(d => d !== difficulty) : [...prev, difficulty]);
  };
  const clearFilters = () => {
    setSelectedRewardTypes([]);
    setSelectedDifficulties([]);
  };
  const handleCloseModal = () => {
    setIsClosing(true);
    setTimeout(() => {
      setShowFilterModal(false);
      setIsClosing(false);
    }, 300);
  };
  const handleCloseCreditsModal = () => {
    setIsCreditsClosing(true);
    setTimeout(() => {
      setShowCreditsModal(false);
      setIsCreditsClosing(false);
    }, 300);
  };
  const handleActivityClick = (activity: Activity) => {
    onNavigate('activity-detail', activity);
  };
  const activeFilterCount = selectedRewardTypes.length + selectedDifficulties.length;
  const hasActiveFilters = activeFilterCount > 0;
  const filteredCategories = useMemo(() => {
    return categories.map(category => {
      const filteredActivities = category.activities.filter(activity => {
        // Filter out activities that user already has
        if (userActionTitles.includes(activity.title.toLowerCase())) {
          return false;
        }
        if (searchQuery && !activity.title.toLowerCase().includes(searchQuery.toLowerCase())) {
          return false;
        }
        if (selectedRewardTypes.length > 0 && !selectedRewardTypes.includes(activity.type)) {
          return false;
        }
        if (selectedDifficulties.length > 0 && !selectedDifficulties.includes(activity.difficulty)) {
          return false;
        }
        return true;
      });
      return {
        ...category,
        activities: filteredActivities
      };
    }).filter(category => category.activities.length > 0);
  }, [searchQuery, selectedRewardTypes, selectedDifficulties, userActionTitles]);
  const totalActivities = filteredCategories.reduce((acc, cat) => acc + cat.activities.length, 0);
  const filteredEvents = useMemo(() => {
    return groupEvents.filter(event => {
      if (searchQuery && !event.title.toLowerCase().includes(searchQuery.toLowerCase())) {
        return false;
      }
      if (selectedRewardTypes.length > 0 && !selectedRewardTypes.includes(event.type)) {
        return false;
      }
      return true;
    });
  }, [searchQuery, selectedRewardTypes]);
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };
  const difficultyLabels = {
    Easy: 'Facile',
    Medium: 'Medio',
    Hard: 'Difficile'
  };
  return <div className="flex flex-col h-screen bg-gray-50 font-sans text-gray-900" data-id="element-740">
      {/* Fixed Header */}
      <header className={`fixed top-0 left-0 right-0 px-6 py-4 flex justify-between items-center z-30 transition-all duration-300 ${isScrolled ? 'bg-white border-b border-gray-100' : 'bg-transparent'}`} data-id="element-741">
        <div className="flex items-center" data-id="element-742">
          <img src="https://cdn.magicpatterns.com/uploads/w4Sqqi6gRHWGegGAQT2PTG/Greenback_logo_(1).png" alt="Greenback Logo" className={`h-8 w-auto transition-all duration-300 ${isScrolled ? '' : 'brightness-0 invert'}`} data-id="element-743" />
        </div>
        <div className="flex items-center gap-3" data-id="element-744">
          <button onClick={() => setShowCreditsModal(true)} className={`flex items-center gap-1.5 transition-colors duration-300 cursor-pointer hover:opacity-80 ${isScrolled ? 'text-green-600' : 'text-white'}`} data-id="element-745">
            <Gem className="h-5 w-5" strokeWidth={2} data-id="element-746" />
            <span className="text-sm font-normal" data-id="element-747">{wallet.diamonds}</span>
          </button>
          <button onClick={() => setShowCreditsModal(true)} className={`flex items-center gap-1.5 transition-colors duration-300 cursor-pointer hover:opacity-80 ${isScrolled ? 'text-gray-600' : 'text-white'}`} data-id="element-748">
            <Star className="h-5 w-5" strokeWidth={2} data-id="element-749" />
            <span className="text-sm font-normal" data-id="element-750">{wallet.stars}</span>
          </button>
        </div>
      </header>

      {/* Main Scrollable Content */}
      <ScrollArea className="flex-1" onScrollCapture={handleScroll} data-id="element-751">
        {/* Hero Section */}
        <div className="bg-green-600 w-full relative pt-20 pb-6" data-id="element-752">
          <div className="absolute inset-0 bg-cover bg-center opacity-20" style={{
          backgroundImage: 'url(https://cdn.magicpatterns.com/uploads/wsCbUWp4TF3ni7DwEfb9qP/hand-drawn-turtle-shell-pattern_23-2150598661.jpg)'
        }} data-id="element-753" />
          <div className="relative z-10 px-6 max-w-md mx-auto" data-id="element-754">
            <div className="mb-5" data-id="element-755">
              <h2 className="font-normal text-2xl text-white mb-1" data-id="element-756">
                Azioni Greenback
              </h2>
              <p className="text-sm text-white/80" data-id="element-757">
                Scegli le azioni da completare
              </p>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="px-6 pb-28 max-w-md mx-auto relative" data-id="element-758">
          {/* Tab Switcher with 3 tabs */}
          <div className="relative -mt-5 mb-4" data-id="element-759">
            <div className="bg-white p-1 rounded-full border border-gray-200 flex overflow-x-auto scrollbar-hide" data-id="element-760">
              <button onClick={() => setActiveTab('solo')} className={`flex-1 py-2 px-4 rounded-full text-sm font-normal transition-all duration-300 whitespace-nowrap ${activeTab === 'solo' ? 'bg-green-600 text-white' : 'text-gray-500 hover:text-gray-900'}`} data-id="element-761">
                Azioni
              </button>
              <button onClick={() => setActiveTab('group')} className={`flex-1 py-2 px-4 rounded-full text-sm font-normal transition-all duration-300 whitespace-nowrap ${activeTab === 'group' ? 'bg-green-600 text-white' : 'text-gray-500 hover:text-gray-900'}`} data-id="element-762">
                Eventi
              </button>
              <button onClick={() => setActiveTab('challenges')} className={`flex-1 py-2 px-4 rounded-full text-sm font-normal transition-all duration-300 whitespace-nowrap ${activeTab === 'challenges' ? 'bg-green-600 text-white' : 'text-gray-500 hover:text-gray-900'}`} data-id="element-763">
                Challenge
              </button>
            </div>
          </div>

          {/* Search Bar and Filter Button */}
          <div className="mb-4" data-id="element-764">
            <div className="flex items-center gap-3 mb-4" data-id="element-765">
              <div className="flex-1 relative" data-id="element-766">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" data-id="element-767" />
                <input type="text" placeholder={activeTab === 'solo' ? 'Cerca attività...' : activeTab === 'group' ? 'Cerca eventi...' : 'Cerca challenge...'} value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full bg-white border border-gray-200 rounded-xl pl-10 pr-4 py-2.5 text-sm font-normal text-gray-700 placeholder:text-gray-400 focus:outline-none focus:border-green-500 transition-colors" data-id="element-768" />
              </div>

              {activeTab === 'solo' && <button onClick={() => setShowFilterModal(true)} className="relative flex items-center justify-center bg-white border border-gray-200 rounded-xl p-2.5 text-gray-700 hover:border-green-500 transition-colors" data-id="element-769">
                  <Filter className="h-5 w-5 text-gray-500" data-id="element-770" />
                  {hasActiveFilters && <span className="absolute -top-1 -right-1 bg-green-600 text-white text-xs font-normal px-1.5 py-0.5 rounded-full min-w-[20px] text-center" data-id="element-771">
                      {activeFilterCount}
                    </span>}
                </button>}
            </div>

            {hasActiveFilters && activeTab === 'solo' && <div className="flex items-center justify-between px-1 mb-2" data-id="element-772">
                <p className="text-xs text-gray-500" data-id="element-773">
                  {totalActivities} attività{' '}
                  {totalActivities === 1 ? 'trovata' : 'trovate'}
                </p>
                <button onClick={clearFilters} className="text-xs font-normal text-red-500 hover:text-red-700 flex items-center gap-1" data-id="element-774">
                  Cancella tutto <X className="h-3 w-3" data-id="element-775" />
                </button>
              </div>}
          </div>

          {/* Content based on active tab */}
          {activeTab === 'solo' ? <div className="space-y-4" data-id="element-776">
              {filteredCategories.length > 0 ? filteredCategories.map(category => {
            const isOpen = openCategories.includes(category.id);
            return <section key={category.id} className="bg-white rounded-xl border border-gray-100 overflow-hidden" data-id="element-777">
                      <button onClick={() => toggleCategory(category.id)} className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors" data-id="element-778">
                        <h2 className="text-base font-normal text-gray-900 flex items-center gap-2" data-id="element-779">
                          {category.title}
                          <span className="text-xs font-normal text-gray-500" data-id="element-780">
                            ({category.activities.length})
                          </span>
                        </h2>
                        <ChevronDown className={`h-5 w-5 text-gray-400 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-781" />
                      </button>

                      {isOpen && <div className="px-3 pb-3" data-id="element-782">
                          <div className="grid grid-cols-2 gap-3" data-id="element-783">
                            {category.activities.map(activity => <div key={activity.id} onClick={() => handleActivityClick(activity)} className="bg-white rounded-xl p-4 border border-gray-100 hover:border-green-500 transition-all duration-200 cursor-pointer group flex flex-col h-full" data-id="element-784">
                                <div className="flex justify-between items-start mb-3" data-id="element-785">
                                  <span className="text-3xl filter drop-shadow-sm group-hover:scale-110 transition-transform duration-200" data-id="element-786">
                                    {activity.emoji}
                                  </span>
                                  <span className={`text-[10px] font-normal px-2 py-1 rounded-full uppercase tracking-wide ${activity.difficulty === 'Easy' ? 'bg-green-100 text-green-700' : activity.difficulty === 'Medium' ? 'bg-yellow-100 text-yellow-700' : 'bg-orange-100 text-orange-700'}`} data-id="element-787">
                                    {difficultyLabels[activity.difficulty]}
                                  </span>
                                </div>

                                <h3 className="font-normal text-sm text-gray-900 mb-1 leading-tight" data-id="element-788">
                                  {activity.title}
                                </h3>

                                <div className="mt-auto pt-2 flex items-center justify-between" data-id="element-789">
                                  <div className={`flex items-center gap-1 text-xs font-normal ${activity.type === 'diamond' ? 'text-green-600' : 'text-gray-400'}`} data-id="element-790">
                                    {activity.type === 'diamond' ? <Gem className="h-3.5 w-3.5" strokeWidth={2} data-id="element-791" /> : <Star className="h-3.5 w-3.5" strokeWidth={2} data-id="element-792" />}
                                    <span data-id="element-793">+{activity.points}</span>
                                  </div>
                                </div>
                              </div>)}
                          </div>
                        </div>}
                    </section>;
          }) : <div className="text-center py-12 bg-white rounded-xl border border-dashed border-gray-300" data-id="element-794">
                  <div className="text-4xl mb-4" data-id="element-795">🔍</div>
                  <h3 className="text-lg font-normal text-gray-900 mb-2" data-id="element-796">
                    Nessuna attività trovata
                  </h3>
                  <p className="text-gray-500 text-sm mb-4" data-id="element-797">
                    Prova a modificare i filtri per vedere più risultati.
                  </p>
                  <button onClick={clearFilters} className="text-green-600 font-normal text-sm hover:underline" data-id="element-798">
                    Cancella tutti i filtri
                  </button>
                </div>}
            </div> : activeTab === 'group' ? <div className="space-y-4" data-id="element-799">
              {filteredEvents.length > 0 ? filteredEvents.map(event => <div key={event.id} className="bg-white rounded-xl border border-gray-100 hover:border-green-500 transition-all duration-200 cursor-pointer overflow-hidden" data-id="element-800">
                    <div className="p-5" data-id="element-801">
                      {/* Event Header */}
                      <div className="flex items-start gap-4 mb-4" data-id="element-802">
                        <div className="text-4xl flex-shrink-0" data-id="element-803">
                          {event.emoji}
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-804">
                          <div className="flex items-start justify-between mb-3" data-id="element-805">
                            <h3 className="font-normal text-base text-gray-900 leading-tight" data-id="element-806">
                              {event.title}
                            </h3>
                            <div className={`flex items-center gap-1 text-xs font-normal ml-2 flex-shrink-0 ${event.type === 'diamond' ? 'text-green-600' : 'text-gray-400'}`} data-id="element-807">
                              {event.type === 'diamond' ? <Gem className="h-3.5 w-3.5" strokeWidth={2} data-id="element-808" /> : <Star className="h-3.5 w-3.5" strokeWidth={2} data-id="element-809" />}
                              <span data-id="element-810">+{event.points}</span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Content aligned with emoji */}
                      <div className="flex gap-4" data-id="element-811">
                        <div className="flex-1 space-y-3" data-id="element-812">
                          {/* Event Type Badge */}
                          <div className="flex items-center gap-2" data-id="element-813">
                            {event.eventType === 'online' ? <span className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 text-xs font-normal px-2 py-1 rounded-full" data-id="element-814">
                                <Video className="h-3 w-3" data-id="element-815" />
                                Online
                              </span> : <span className="inline-flex items-center gap-1 bg-purple-100 text-purple-700 text-xs font-normal px-2 py-1 rounded-full" data-id="element-816">
                                <MapPin className="h-3 w-3" data-id="element-817" />
                                In presenza
                              </span>}
                          </div>

                          {/* Description */}
                          <p className="text-sm text-gray-600 leading-relaxed" data-id="element-818">
                            {event.description}
                          </p>

                          {/* Event Details */}
                          <div className="space-y-2" data-id="element-819">
                            <div className="flex items-center gap-2 text-xs text-gray-600" data-id="element-820">
                              <Calendar className="h-4 w-4 text-gray-400" data-id="element-821" />
                              <span className="font-normal" data-id="element-822">
                                {formatDate(event.date)}
                              </span>
                              <span className="text-gray-400" data-id="element-823">•</span>
                              <Clock className="h-4 w-4 text-gray-400" data-id="element-824" />
                              <span data-id="element-825">{event.time}</span>
                            </div>

                            {event.location && <div className="flex items-center gap-2 text-xs text-gray-600" data-id="element-826">
                                <MapPin className="h-4 w-4 text-gray-400" data-id="element-827" />
                                <span data-id="element-828">{event.location}</span>
                              </div>}

                            <div className="flex items-center gap-2 text-xs text-gray-600" data-id="element-829">
                              <Users className="h-4 w-4 text-gray-400" data-id="element-830" />
                              <span data-id="element-831">
                                <span className="font-normal text-green-600" data-id="element-832">
                                  {event.participants}
                                </span>
                                /{event.maxParticipants} partecipanti
                              </span>
                            </div>
                          </div>

                          {/* Organizer */}
                          <div className="pt-3 border-t border-gray-100" data-id="element-833">
                            <p className="text-xs text-gray-500" data-id="element-834">
                              Organizzato da{' '}
                              <span className="font-normal text-gray-700" data-id="element-835">
                                {event.organizer}
                              </span>
                            </p>
                          </div>

                          {/* Participants Progress Bar */}
                          <div data-id="element-836">
                            <div className="w-full bg-gray-200 rounded-full h-1.5" data-id="element-837">
                              <div className="bg-green-600 h-full rounded-full transition-all duration-500" style={{
                        width: `${event.participants / event.maxParticipants * 100}%`
                      }} data-id="element-838" />
                            </div>
                          </div>

                          {/* CTA Button */}
                          <button className="w-full py-2.5 px-4 rounded-full text-sm font-normal text-white bg-green-600 hover:bg-green-700 transition-all" data-id="element-839">
                            Partecipa all'evento
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>) : <div className="text-center py-12 bg-white rounded-xl border border-dashed border-gray-300" data-id="element-840">
                  <div className="text-4xl mb-4" data-id="element-841">📅</div>
                  <h3 className="text-lg font-normal text-gray-900 mb-2" data-id="element-842">
                    Nessun evento trovato
                  </h3>
                  <p className="text-gray-500 text-sm" data-id="element-843">
                    Prova a modificare la ricerca per vedere più eventi.
                  </p>
                </div>}
            </div> : <div className="space-y-6" data-id="element-844">
              {/* Active Challenges */}
              {activeChallenges.length > 0 && <div data-id="element-845">
                  <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3 px-1" data-id="element-846">
                    In Corso
                  </h3>
                  <div className="space-y-3" data-id="element-847">
                    {activeChallenges.map(challenge => <div key={challenge.id} className="bg-white rounded-xl p-4 border border-gray-100" data-id="element-848">
                        <div className="flex items-start gap-3 mb-3" data-id="element-849">
                          <div className="text-3xl" data-id="element-850">{challenge.emoji}</div>
                          <div className="flex-1" data-id="element-851">
                            <h4 className="font-normal text-sm text-gray-900 mb-1" data-id="element-852">
                              {challenge.title}
                            </h4>
                            <p className="text-xs text-gray-500 mb-2" data-id="element-853">
                              {challenge.description}
                            </p>
                            <div className="flex items-center gap-2 text-xs text-gray-500" data-id="element-854">
                              <Calendar className="h-3 w-3" data-id="element-855" />
                              <span data-id="element-856">{challenge.duration}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 text-xs font-normal text-green-600" data-id="element-857">
                            {challenge.type === 'diamond' ? <Gem className="h-3.5 w-3.5" strokeWidth={2} data-id="element-858" /> : <Star className="h-3.5 w-3.5" strokeWidth={2} data-id="element-859" />}
                            <span data-id="element-860">+{challenge.points}</span>
                          </div>
                        </div>

                        {/* Progress Bar */}
                        <div className="space-y-2" data-id="element-861">
                          <div className="flex items-center justify-between text-xs" data-id="element-862">
                            <span className="text-gray-600" data-id="element-863">Progresso</span>
                            <span className="font-normal text-gray-900" data-id="element-864">
                              {challenge.progress}/{challenge.total}
                            </span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2" data-id="element-865">
                            <div className="bg-green-600 h-full rounded-full transition-all duration-500" style={{
                      width: `${challenge.progress / challenge.total * 100}%`
                    }} data-id="element-866" />
                          </div>
                        </div>
                      </div>)}
                  </div>
                </div>}

              {/* Available Challenges */}
              {availableChallenges.length > 0 && <div data-id="element-867">
                  <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-3 px-1" data-id="element-868">
                    Disponibili
                  </h3>
                  <div className="space-y-3" data-id="element-869">
                    {availableChallenges.map(challenge => <div key={challenge.id} className="bg-white rounded-xl p-4 border border-gray-100 hover:border-green-500 transition-colors" data-id="element-870">
                        <div className="flex items-start gap-3 mb-3" data-id="element-871">
                          <div className="text-3xl" data-id="element-872">{challenge.emoji}</div>
                          <div className="flex-1" data-id="element-873">
                            <h4 className="font-normal text-sm text-gray-900 mb-1" data-id="element-874">
                              {challenge.title}
                            </h4>
                            <p className="text-xs text-gray-500 mb-2" data-id="element-875">
                              {challenge.description}
                            </p>
                            <div className="flex items-center gap-2 text-xs text-gray-500" data-id="element-876">
                              <Calendar className="h-3 w-3" data-id="element-877" />
                              <span data-id="element-878">{challenge.duration}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 text-xs font-normal text-green-600" data-id="element-879">
                            {challenge.type === 'diamond' ? <Gem className="h-3.5 w-3.5" strokeWidth={2} data-id="element-880" /> : <Star className="h-3.5 w-3.5" strokeWidth={2} data-id="element-881" />}
                            <span data-id="element-882">+{challenge.points}</span>
                          </div>
                        </div>

                        <button onClick={() => handleAcceptChallenge(challenge.id)} className="w-full py-2.5 px-4 rounded-full text-sm font-normal text-white bg-green-600 hover:bg-green-700 transition-all" data-id="element-883">
                          Accetta Sfida
                        </button>
                      </div>)}
                  </div>
                </div>}
            </div>}
        </div>
      </ScrollArea>

      <BottomNav activePage="impact" onNavigate={page => onNavigate(page)} data-id="element-884" />

      {/* Credits Modal */}
      {showCreditsModal && <>
          <div className="fixed inset-0 bg-black/50 z-40 transition-opacity" onClick={handleCloseCreditsModal} data-id="element-885" />

          <div className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 max-w-md mx-auto max-h-[85vh] overflow-hidden flex flex-col border-t border-gray-100 ${isCreditsClosing ? 'animate-slide-down' : 'animate-slide-up'}`} data-id="element-886">
            <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-gray-100" data-id="element-887">
              <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-6" data-id="element-888" />
              <div className="flex items-center justify-between" data-id="element-889">
                <h3 className="text-xl font-normal text-gray-900" data-id="element-890">
                  Come funzionano i crediti
                </h3>
                <button onClick={handleCloseCreditsModal} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-891">
                  <X className="h-6 w-6" strokeWidth={1.5} data-id="element-892" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6" data-id="element-893">
              <div className="space-y-6" data-id="element-894">
                <div data-id="element-895">
                  <h4 className="font-normal text-base text-gray-900 mb-4" data-id="element-896">
                    Livelli di verifica delle azioni
                  </h4>
                  <div className="space-y-3" data-id="element-897">
                    <div className="bg-white rounded-xl p-4 border border-gray-200" data-id="element-898">
                      <div className="flex items-start gap-3" data-id="element-899">
                        <FileCheck className="h-5 w-5 text-gray-400 flex-shrink-0" strokeWidth={2} data-id="element-900" />
                        <div className="flex-1" data-id="element-901">
                          <h5 className="font-normal text-sm text-gray-900 mb-1" data-id="element-902">
                            Non verificate
                          </h5>
                          <p className="text-xs text-gray-600 leading-relaxed" data-id="element-903">
                            Azioni tracciate per statistiche e profilazione. Non
                            danno crediti.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-xl p-4 border border-gray-200" data-id="element-904">
                      <div className="flex items-start gap-3" data-id="element-905">
                        <Award className="h-5 w-5 text-yellow-500 flex-shrink-0" strokeWidth={2} data-id="element-906" />
                        <div className="flex-1" data-id="element-907">
                          <h5 className="font-normal text-sm text-gray-900 mb-1" data-id="element-908">
                            Verificate
                          </h5>
                          <p className="text-xs text-gray-600 leading-relaxed" data-id="element-909">
                            Verifica tramite auto-certificazione. Danno crediti
                            Stelle.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white rounded-xl p-4 border border-gray-200" data-id="element-910">
                      <div className="flex items-start gap-3" data-id="element-911">
                        <Shield className="h-5 w-5 text-green-600 flex-shrink-0" strokeWidth={2} data-id="element-912" />
                        <div className="flex-1" data-id="element-913">
                          <h5 className="font-normal text-sm text-gray-900 mb-1" data-id="element-914">
                            Certificate
                          </h5>
                          <p className="text-xs text-gray-600 leading-relaxed" data-id="element-915">
                            Certificate da Greenback o partner ufficiali.
                            Registrate su Blockchain. Danno crediti Diamond.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div data-id="element-916">
                  <h4 className="font-normal text-base text-gray-900 mb-4" data-id="element-917">
                    Cosa puoi fare con i crediti
                  </h4>
                  <div className="space-y-3" data-id="element-918">
                    <div className="bg-white rounded-xl p-5 border border-gray-200" data-id="element-919">
                      <div className="flex items-start gap-3 mb-3" data-id="element-920">
                        <Star className="h-6 w-6 text-yellow-500 flex-shrink-0" strokeWidth={2} fill="currentColor" data-id="element-921" />
                        <div className="flex-1" data-id="element-922">
                          <h4 className="font-normal text-base text-gray-900 mb-1" data-id="element-923">
                            Crediti Stelle
                          </h4>
                          <p className="text-xs text-gray-600 leading-relaxed" data-id="element-924">
                            Valore non monetario
                          </p>
                        </div>
                      </div>
                      <div className="space-y-2" data-id="element-925">
                        <p className="text-xs text-gray-700" data-id="element-926">
                          <span className="font-medium" data-id="element-927">✓</span> Partecipazione
                          a eventi esclusivi
                        </p>
                        <p className="text-xs text-gray-700" data-id="element-928">
                          <span className="font-medium" data-id="element-929">✓</span> Sblocco di
                          feature premium nell'app
                        </p>
                        <p className="text-xs text-gray-700" data-id="element-930">
                          <span className="font-medium" data-id="element-931">✓</span> Accesso a
                          contenuti riservati
                        </p>
                      </div>
                    </div>

                    <div className="bg-white rounded-xl p-5 border border-gray-200" data-id="element-932">
                      <div className="flex items-start gap-3 mb-3" data-id="element-933">
                        <Gem className="h-6 w-6 text-green-600 flex-shrink-0" strokeWidth={2} data-id="element-934" />
                        <div className="flex-1" data-id="element-935">
                          <h4 className="font-normal text-base text-gray-900 mb-1" data-id="element-936">
                            Crediti Diamond
                          </h4>
                          <p className="text-xs text-gray-600 leading-relaxed" data-id="element-937">
                            Valore monetario e ufficiale
                          </p>
                        </div>
                      </div>
                      <div className="space-y-2" data-id="element-938">
                        <p className="text-xs text-gray-700" data-id="element-939">
                          <span className="font-medium" data-id="element-940">✓</span> Convertibili in
                          buoni acquisto
                        </p>
                        <p className="text-xs text-gray-700" data-id="element-941">
                          <span className="font-medium" data-id="element-942">✓</span> Convertibili in
                          denaro
                        </p>
                        <p className="text-xs text-gray-700" data-id="element-943">
                          <span className="font-medium" data-id="element-944">✓</span> Certificazione
                          su Blockchain
                        </p>
                        <p className="text-xs text-gray-700" data-id="element-945">
                          <span className="font-medium" data-id="element-946">✓</span> Riconoscimento
                          ufficiale e formale
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-xl p-4 border border-blue-200" data-id="element-947">
                  <div className="flex items-start gap-3" data-id="element-948">
                    <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" strokeWidth={2} data-id="element-949" />
                    <div data-id="element-950">
                      <p className="text-xs text-blue-900 leading-relaxed" data-id="element-951">
                        <span className="font-medium" data-id="element-952">Nota:</span> Le azioni
                        certificate richiedono una verifica ufficiale da parte
                        di Greenback o dei nostri partner autorizzati. Una volta
                        certificate, vengono registrate permanentemente su
                        Blockchain per garantire trasparenza e autenticità.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>}

      {/* Filter Modal */}
      {showFilterModal && activeTab === 'solo' && <>
          <div className="fixed inset-0 bg-black/50 z-40 transition-opacity" onClick={handleCloseModal} data-id="element-953" />

          <div className={`fixed bottom-0 left-0 right-0 bg-white rounded-t-3xl z-50 max-w-md mx-auto max-h-[75vh] overflow-hidden flex flex-col border-t border-gray-100 ${isClosing ? 'animate-slide-down' : 'animate-slide-up'}`} data-id="element-954">
            <div className="flex-shrink-0 px-6 pt-6 pb-4 border-b border-gray-100" data-id="element-955">
              <div className="w-12 h-1 bg-gray-300 rounded-full mx-auto mb-6" data-id="element-956" />
              <div className="flex items-center justify-between" data-id="element-957">
                <h3 className="text-xl font-normal text-gray-900" data-id="element-958">
                  Filtra le attività
                </h3>
                <button onClick={handleCloseModal} className="text-gray-400 hover:text-gray-600 transition-colors" data-id="element-959">
                  <X className="h-6 w-6" strokeWidth={1.5} data-id="element-960" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-6 space-y-8" data-id="element-961">
              <section data-id="element-962">
                <h4 className="text-sm font-normal text-gray-900 mb-3 uppercase tracking-wide" data-id="element-963">
                  Tipo di ricompensa
                </h4>
                <div className="flex flex-wrap gap-3" data-id="element-964">
                  <button onClick={() => toggleRewardFilter('diamond')} className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-normal transition-all border ${selectedRewardTypes.includes('diamond') ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-600 border-gray-200 hover:border-green-300'}`} data-id="element-965">
                    <Gem className="h-4 w-4" strokeWidth={2} data-id="element-966" />
                    Punti diamante
                    {selectedRewardTypes.includes('diamond') && <Check className="h-4 w-4" data-id="element-967" />}
                  </button>
                  <button onClick={() => toggleRewardFilter('gold')} className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-normal transition-all border ${selectedRewardTypes.includes('gold') ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-600 border-gray-200 hover:border-green-300'}`} data-id="element-968">
                    <Star className="h-4 w-4" strokeWidth={2} fill="currentColor" data-id="element-969" />
                    Punti standard
                    {selectedRewardTypes.includes('gold') && <Check className="h-4 w-4" data-id="element-970" />}
                  </button>
                </div>
              </section>

              <section data-id="element-971">
                <h4 className="text-sm font-normal text-gray-900 mb-3 uppercase tracking-wide" data-id="element-972">
                  Livello di difficoltà
                </h4>
                <div className="flex flex-wrap gap-3" data-id="element-973">
                  {['Easy', 'Medium', 'Hard'].map(level => <button key={level} onClick={() => toggleDifficultyFilter(level as 'Easy' | 'Medium' | 'Hard')} className={`flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-normal transition-all border ${selectedDifficulties.includes(level as any) ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-600 border-gray-200 hover:border-green-300'}`} data-id="element-974">
                      {difficultyLabels[level as keyof typeof difficultyLabels]}
                      {selectedDifficulties.includes(level as any) && <Check className="h-4 w-4" data-id="element-975" />}
                    </button>)}
                </div>
              </section>
            </div>

            <div className="flex-shrink-0 p-6 border-t border-gray-100 bg-white pb-safe" data-id="element-976">
              <div className="flex gap-3" data-id="element-977">
                {hasActiveFilters && <button onClick={clearFilters} className="flex-1 py-3.5 px-4 rounded-full text-sm font-normal text-red-600 bg-red-50 hover:bg-red-100 transition-colors border border-red-100" data-id="element-978">
                    Cancella tutto
                  </button>}
                <button onClick={handleCloseModal} className="flex-[2] py-3.5 px-4 rounded-full text-sm font-normal text-white bg-green-600 hover:bg-green-700 transition-all" data-id="element-979">
                  Mostra {totalActivities} attività
                </button>
              </div>
            </div>
          </div>
        </>}
    </div>;
}